import React, { useState, useEffect } from "react";
import { CheckCircle2, Download, Gift, Heart, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ThankYouPage = () => {
  const ZAPIER_WEBHOOK_URL = process.env.REACT_APP_ZAPIER_WEBHOOK_URL;
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is registered, redirect if not
    const storedEmail = sessionStorage.getItem("registeredEmail");
    if (!storedEmail) {
      navigate("/");
      return;
    }
    setRegisteredEmail(storedEmail);
  }, [navigate]);

  const handleDownload = async () => {
    setIsLoading(true);

    try {
      // Track download in CRM
      const downloadData = {
        email: registeredEmail,
        action: "PDF Downloaded",
        content_type: "PDF Checklist",
        timestamp: new Date().toISOString(),
        source_url: window.location.href,
        registered_for_webinar: true,
        download_method: "Thank You Page",
      };

      await fetch(ZAPIER_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify(downloadData),
      });

      // Start PDF download
      const link = document.createElement("a");
      link.href = "/downloads/27-Point-Home-Buyers-Checklist.pdf";
      link.download = "27-Point-Home-Buyers-Checklist.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Wait a moment then redirect back to main page
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      console.error("Error tracking download:", error);
      // Still allow download even if tracking fails
      const link = document.createElement("a");
      link.href = "/downloads/27-Point-Home-Buyers-Checklist.pdf";
      link.download = "27-Point-Home-Buyers-Checklist.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Redirect after download
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center py-6 sm:py-12 px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full mb-6 sm:mb-8 shadow-2xl animate-bounce">
            <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
          </div>

          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-2">
            Registration Successful!
          </h1>

          <p className="text-lg sm:text-2xl md:text-3xl text-gray-700 mb-3 sm:mb-4 font-medium px-2">
            Welcome to our exclusive webinar community!
          </p>

          <p className="text-sm sm:text-lg text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
            Thank you, <strong>{registeredEmail}</strong>! As promised, here's
            your
            <strong> FREE 27-Point Home Buyer's Checklist</strong> - a
            comprehensive guide that has helped thousands of first-time buyers
            successfully purchase their dream home.
          </p>

          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-12 shadow-2xl border border-green-200 max-w-2xl mx-auto transform hover:scale-105 transition-all duration-300 mb-6 sm:mb-8">
            <div className="flex items-center justify-center mb-6 sm:mb-8">
              <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-4 sm:p-6 rounded-2xl">
                <Gift className="w-12 h-12 sm:w-16 sm:h-16 text-green-600" />
              </div>
            </div>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Your Premium Checklist
            </h2>

            <div className="text-left mb-6 sm:mb-8 space-y-2 sm:space-y-3">
              <p className="text-sm sm:text-base text-gray-700 flex items-start">
                <span className="text-green-600 mr-2 sm:mr-3 text-base sm:text-lg">
                  •
                </span>
                ✅ Pre-approval strategies that get you approved faster
              </p>
              <p className="text-sm sm:text-base text-gray-700 flex items-start">
                <span className="text-green-600 mr-2 sm:mr-3 text-base sm:text-lg">
                  •
                </span>
                ✅ Hidden costs most first-time buyers miss
              </p>
              <p className="text-sm sm:text-base text-gray-700 flex items-start">
                <span className="text-green-600 mr-2 sm:mr-3 text-base sm:text-lg">
                  •
                </span>
                ✅ Inspection red flags that could save you $10,000+
              </p>
              <p className="text-sm sm:text-base text-gray-700 flex items-start">
                <span className="text-green-600 mr-2 sm:mr-3 text-base sm:text-lg">
                  •
                </span>
                ✅ Negotiation tactics that work in any market
              </p>
              <p className="text-sm sm:text-base text-gray-700 flex items-start">
                <span className="text-green-600 mr-2 sm:mr-3 text-base sm:text-lg">
                  •
                </span>
                ✅ Closing day preparation & document checklist
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <button
                onClick={handleDownload}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 sm:py-4 px-6 sm:px-8 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg hover:from-green-700 hover:to-emerald-700 focus:ring-4 focus:ring-green-300 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation">
                <div className="flex items-center justify-center">
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm sm:text-base">
                        Preparing Download & Redirecting...
                      </span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                      <span className="text-sm sm:text-base">
                        Download Your FREE Checklist (PDF)
                      </span>
                    </>
                  )}
                </div>
              </button>

              <button
                onClick={handleBackToHome}
                className="w-full bg-gray-100 text-gray-700 py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center touch-manipulation">
                <Home className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                <span className="text-sm sm:text-base">
                  Back to Webinar Page
                </span>
              </button>
            </div>

            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm text-gray-500">
              <span className="flex items-center">
                <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-500" />
                Valued at $197
              </span>
              <span className="hidden sm:inline">•</span>
              <span>Instant Download</span>
              <span className="hidden sm:inline">•</span>
              <span>Professional PDF</span>
            </div>
          </div>

          <div className="bg-white/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-xl mx-auto">
            <p className="text-gray-700 text-sm sm:text-lg leading-relaxed">
              <strong>What's Next?</strong> Check your email for webinar access
              details. We'll see you at the live session where you'll learn
              advanced strategies to use this checklist effectively!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
